import './AppVersions.scss';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import Button from '../../components/Buttons/Button';
import Skeleton from '../../components/Skeleton/Skeleton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AppVersions = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newPlatform, setNewPlatform] = useState('ios');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [breakdown, setBreakdown] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);

  // AppConfig values that actually drive the mobile gates. The version
  // history table below is a release log — it does NOT change mobile
  // behavior on its own. These two inputs do.
  const [appConfig, setAppConfig] = useState({ minAppVersion: '', latestAppVersion: '' });
  const [appConfigLoading, setAppConfigLoading] = useState(true);
  const [minVersionDraft, setMinVersionDraft] = useState('');
  const [latestVersionDraft, setLatestVersionDraft] = useState('');
  const [savingMin, setSavingMin] = useState(false);
  const [savingLatest, setSavingLatest] = useState(false);
  // Homepage venue video circle size, in percent of the flyer height (50-100).
  const [videoSizePct, setVideoSizePct] = useState(100);
  const [videoSizeDraft, setVideoSizeDraft] = useState(100);
  // Only a whole number from 50 to 100 can be saved; an empty box never resets to 100.
  const videoSizeValid = String(videoSizeDraft).trim() !== '' && Number(videoSizeDraft) >= 50 && Number(videoSizeDraft) <= 100;
  const [savingVideoSize, setSavingVideoSize] = useState(false);
  // In-app purchases (Plan boost, paid shuffle): the switch and RevenueCat's public SDK keys.
  const [iap, setIap] = useState(null);
  const [iosKeyDraft, setIosKeyDraft] = useState('');
  const [androidKeyDraft, setAndroidKeyDraft] = useState('');
  const [savingIap, setSavingIap] = useState(false);
  const [iapLoadFailed, setIapLoadFailed] = useState(false);

  const fetchAppConfig = useCallback(async () => {
    setAppConfigLoading(true);
    try {
      const { data } = await api.get('/app-config');
      const min = data?.minAppVersion || '';
      const latest = data?.latestAppVersion || '';
      setAppConfig({ minAppVersion: min, latestAppVersion: latest });
      setMinVersionDraft(min);
      setLatestVersionDraft(latest);
      const pct = Math.round((Number(data?.rowVideo?.scale) || 1) * 100);
      setVideoSizePct(pct);
      setVideoSizeDraft(pct);
    } catch (error) {
      console.error('Error fetching app config:', error);
    } finally {
      setAppConfigLoading(false);
    }
  }, []);

  const saveMinVersion = async () => {
    const v = minVersionDraft.trim();
    if (!v) { toast.error('Version is required'); return; }
    setSavingMin(true);
    try {
      await api.put('/app-config/min-version', { version: v });
      setAppConfig((prev) => ({ ...prev, minAppVersion: v }));
      toast.success(`Force-update floor set to ${v}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSavingMin(false);
    }
  };

  const saveLatestVersion = async () => {
    const v = latestVersionDraft.trim();
    if (!v) { toast.error('Version is required'); return; }
    setSavingLatest(true);
    try {
      await api.put('/app-config/latest-version', { version: v });
      setAppConfig((prev) => ({ ...prev, latestAppVersion: v }));
      toast.success(`Soft-update target set to ${v}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSavingLatest(false);
    }
  };

  const saveVideoSize = async () => {
    const pct = Math.max(50, Math.min(100, Math.round(Number(videoSizeDraft) || 100)));
    setSavingVideoSize(true);
    try {
      const { data } = await api.put('/app-config/row-video', { scale: pct / 100 });
      const saved = Math.round((Number(data?.rowVideo?.scale) || pct / 100) * 100);
      setVideoSizePct(saved);
      setVideoSizeDraft(saved);
      toast.success(`Homepage video size set to ${saved}%`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSavingVideoSize(false);
    }
  };

  const fetchIap = useCallback(async () => {
    setIapLoadFailed(false);
    try {
      const { data } = await api.get('/app-config/iap');
      setIap(data);
      setIosKeyDraft(data?.iosApiKey || '');
      setAndroidKeyDraft(data?.androidApiKey || '');
    } catch (error) {
      console.error('Error fetching in-app purchase settings:', error);
      setIapLoadFailed(true);
    }
  }, []);

  const saveIap = async (body, message) => {
    setSavingIap(true);
    try {
      const { data } = await api.put('/app-config/iap', body);
      setIap(data);
      // Only the boxes that were saved take the server's value; an unsaved draft stays.
      if ('iosApiKey' in body) setIosKeyDraft(data?.iosApiKey || '');
      if ('androidApiKey' in body) setAndroidKeyDraft(data?.androidApiKey || '');
      toast.success(message);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSavingIap(false);
    }
  };

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const [versionsRes, breakdownRes] = await Promise.all([
        api.get('/app-version/search', { params: { records: 50 } }),
        api.get('/app-version/user-breakdown').catch(() => ({ data: null })),
      ]);
      setVersions(versionsRes.data.data || []);
      setBreakdown(breakdownRes.data);
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVersions(); fetchAppConfig(); fetchIap(); }, [fetchVersions, fetchAppConfig, fetchIap]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newVersion.trim()) {
      toast.error('Version number is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/app-version/set-version', {
        version: newVersion.trim(),
        platform: newPlatform,
        updateRequired: forceUpdate,
      });
      toast.success(`Version ${newVersion} set for ${newPlatform}`);
      setNewVersion('');
      setForceUpdate(false);
      setShowForm(false);
      fetchVersions();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to set version';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  // Source of truth for "latest" is real user adoption (User.app_version
  // + User.app_platform aggregation), not the manually-maintained
  // AppVersionHistory table that nobody remembered to update. The old
  // AppVersionHistory rows still render in the release log below.
  const latestIos = breakdown?.latest_ios
    ? { version: breakdown.latest_ios }
    : versions.find(v => v.platform === 'ios');
  const latestAndroid = breakdown?.latest_android
    ? { version: breakdown.latest_android }
    : versions.find(v => v.platform === 'android');
  const recommendedForce = breakdown?.recommended_force_update_floor || null;
  const recommendedSoft = breakdown?.recommended_soft_update_target || null;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="app-versions">
            <div className="app-versions__header">
              <div>
                <h1>App Versions</h1>
                <p className="app-versions__subtitle">Manage force updates and version history</p>
              </div>
              <Button primaryBtn onClick={() => setShowForm(!showForm)}>
                {showForm ? 'Cancel' : 'Set New Version'}
              </Button>
            </div>

            {/* Mobile update prompts — the two values that actually drive
                what the app shows. Force floor blocks below it; Soft target
                nudges (dismissible banner) between floor and target. */}
            <div className="app-versions__prompts">
              <h2>Mobile Update Prompts</h2>
              <p className="app-versions__prompts-help">
                These two values drive what the app shows. The version log below is just a release record.
              </p>

              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">Force-update floor</div>
                  <div className="app-versions__prompt-sub">
                    Users below this see the full-screen blocker. Current: <strong>{appConfigLoading ? '…' : (appConfig.minAppVersion || '—')}</strong>
                    {recommendedForce && (
                      <>
                        {' · Recommended: '}
                        <strong style={{ color: '#B6FF3C' }}>{recommendedForce}</strong>
                        <span style={{ opacity: 0.5 }}> (1 version behind latest)</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="app-versions__prompt-controls">
                  <input
                    type="text"
                    placeholder="e.g. 2.0.10"
                    value={minVersionDraft}
                    onChange={(e) => setMinVersionDraft(e.target.value)}
                    disabled={appConfigLoading}
                  />
                  {recommendedForce && minVersionDraft !== recommendedForce && (
                    <Button
                      onClick={() => setMinVersionDraft(recommendedForce)}
                      disabled={appConfigLoading}
                      style={{ background: 'transparent', border: '1px solid rgba(182,255,60,0.4)', color: '#B6FF3C' }}>
                      Use recommended
                    </Button>
                  )}
                  <Button
                    primaryBtn
                    onClick={saveMinVersion}
                    disabled={savingMin || appConfigLoading || minVersionDraft.trim() === appConfig.minAppVersion}
                    showLoading={savingMin}>
                    Save
                  </Button>
                </div>
              </div>

              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">Soft-update target</div>
                  <div className="app-versions__prompt-sub">
                    Users on a lower version (but above the floor) see a dismissible banner. Current: <strong>{appConfigLoading ? '…' : (appConfig.latestAppVersion || '—')}</strong>
                    {recommendedSoft && (
                      <>
                        {' · Recommended: '}
                        <strong style={{ color: '#B6FF3C' }}>{recommendedSoft}</strong>
                        <span style={{ opacity: 0.5 }}> (latest live version)</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="app-versions__prompt-controls">
                  <input
                    type="text"
                    placeholder="e.g. 2.0.11"
                    value={latestVersionDraft}
                    onChange={(e) => setLatestVersionDraft(e.target.value)}
                    disabled={appConfigLoading}
                  />
                  {recommendedSoft && latestVersionDraft !== recommendedSoft && (
                    <Button
                      onClick={() => setLatestVersionDraft(recommendedSoft)}
                      disabled={appConfigLoading}
                      style={{ background: 'transparent', border: '1px solid rgba(182,255,60,0.4)', color: '#B6FF3C' }}>
                      Use recommended
                    </Button>
                  )}
                  <Button
                    primaryBtn
                    onClick={saveLatestVersion}
                    disabled={savingLatest || appConfigLoading || latestVersionDraft.trim() === appConfig.latestAppVersion}
                    showLoading={savingLatest}>
                    Save
                  </Button>
                </div>
              </div>
            </div>

            {/* Homepage venue video circle. Open apps resize live when this saves. */}
            <div className="app-versions__prompts">
              <h2>Homepage</h2>
              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">Homepage video size</div>
                  <div className="app-versions__prompt-sub">
                    The venue video circle, as a share of the flyer height. 100% is the shipped size. Current: <strong>{appConfigLoading ? '…' : `${videoSizePct}%`}</strong>
                  </div>
                </div>
                <div className="app-versions__prompt-controls">
                  <input
                    type="range"
                    min={50}
                    max={100}
                    step={1}
                    value={videoSizeDraft}
                    onChange={(e) => setVideoSizeDraft(Number(e.target.value))}
                    disabled={appConfigLoading}
                    aria-label="Homepage video size"
                  />
                  <input
                    type="number"
                    min={50}
                    max={100}
                    step={1}
                    value={videoSizeDraft}
                    onChange={(e) => setVideoSizeDraft(e.target.value === '' ? '' : Number(e.target.value))}
                    disabled={appConfigLoading}
                    style={{ width: 72 }}
                  />
                  <span>%</span>
                  <Button
                    primaryBtn
                    onClick={saveVideoSize}
                    disabled={savingVideoSize || appConfigLoading || !videoSizeValid || Number(videoSizeDraft) === videoSizePct}
                    showLoading={savingVideoSize}>
                    Save
                  </Button>
                </div>
              </div>
            </div>

            {/* In-app purchases. Off until the store setup is done; the app reads these with no build. */}
            <div className="app-versions__prompts">
              <h2>In-app purchases</h2>
              <p className="app-versions__prompts-help">
                Plan boost and the paid Meet People shuffle. The app shows them only when this is on, the keys are saved and the server has its RevenueCat secrets.
                {' '}Server secrets: <strong>{iap ? (iap.serverReady ? 'set' : 'not set yet') : '…'}</strong>
                {iap && iap.testers ? ` · Testers: ${iap.testers}` : ''}
              </p>
              {iapLoadFailed && (
                <p className="app-versions__prompts-help">
                  Couldn't load these settings.{' '}
                  <Button onClick={fetchIap}>Try again</Button>
                </p>
              )}

              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">iOS public SDK key</div>
                  <div className="app-versions__prompt-sub">RevenueCat, starts with appl_</div>
                </div>
                <div className="app-versions__prompt-controls">
                  <input
                    type="text"
                    placeholder="appl_..."
                    value={iosKeyDraft}
                    onChange={(e) => setIosKeyDraft(e.target.value)}
                    disabled={!iap}
                  />
                </div>
              </div>

              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">Android public SDK key</div>
                  <div className="app-versions__prompt-sub">RevenueCat, starts with goog_</div>
                </div>
                <div className="app-versions__prompt-controls">
                  <input
                    type="text"
                    placeholder="goog_..."
                    value={androidKeyDraft}
                    onChange={(e) => setAndroidKeyDraft(e.target.value)}
                    disabled={!iap}
                  />
                  <Button
                    primaryBtn
                    onClick={() => saveIap({ iosApiKey: iosKeyDraft.trim(), androidApiKey: androidKeyDraft.trim() }, 'Keys saved')}
                    disabled={!iap || savingIap || (iosKeyDraft.trim() === (iap.iosApiKey || '') && androidKeyDraft.trim() === (iap.androidApiKey || ''))}
                    showLoading={savingIap}>
                    Save keys
                  </Button>
                </div>
              </div>

              <div className="app-versions__prompt-row">
                <div className="app-versions__prompt-label">
                  <div className="app-versions__prompt-title">Purchases for everyone</div>
                  <div className="app-versions__prompt-sub">
                    Current: <strong>{iap ? (iap.enabled ? 'On' : 'Off') : '…'}</strong>
                  </div>
                </div>
                <div className="app-versions__prompt-controls">
                  <Button
                    primaryBtn
                    onClick={() => saveIap({ enabled: !iap.enabled }, iap.enabled ? 'Purchases turned off' : 'Purchases turned on')}
                    disabled={!iap || savingIap}
                    showLoading={savingIap}>
                    {iap && iap.enabled ? 'Turn off' : 'Turn on'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Current versions */}
            <div className="app-versions__current">
              <div className="app-versions__current-card">
                <div className="app-versions__current-icon">
                  <span className="iconify" data-icon="ic:baseline-apple" style={{ fontSize: 28, color: '#fff' }}></span>
                </div>
                <div>
                  <div className="app-versions__current-label">iOS Latest</div>
                  <div className="app-versions__current-version">{latestIos?.version || '—'}</div>
                  {latestIos?.updateRequired && (
                    <span className="app-versions__force-badge">Force Update</span>
                  )}
                </div>
              </div>
              <div className="app-versions__current-card">
                <div className="app-versions__current-icon" style={{ background: 'rgba(46, 204, 113, 0.15)' }}>
                  <span className="iconify" data-icon="mdi:android" style={{ fontSize: 28, color: '#2ECC71' }}></span>
                </div>
                <div>
                  <div className="app-versions__current-label">Android Latest</div>
                  <div className="app-versions__current-version">{latestAndroid?.version || '—'}</div>
                  {latestAndroid?.updateRequired && (
                    <span className="app-versions__force-badge">Force Update</span>
                  )}
                </div>
              </div>
            </div>

            {/* User version breakdown */}
            {breakdown?.data?.length > 0 && (
              <div className="app-versions__breakdown">
                <h2>Who needs to update?</h2>
                <div className="app-versions__breakdown-stats">
                  <span>{breakdown.users_with_version} users tracked</span>
                  <span>·</span>
                  <span>{breakdown.users_without_version} unknown version</span>
                </div>
                <div className="app-versions__breakdown-list">
                  {breakdown.data.map((b) => (
                    <div key={b.version} className="app-versions__breakdown-item">
                      <div
                        className="app-versions__breakdown-row"
                        onClick={() => setExpandedVersion(expandedVersion === b.version ? null : b.version)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="app-versions__breakdown-version">
                          <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{b.version}</span>
                          {b.is_latest && <span className="app-versions__latest-badge">Latest</span>}
                        </div>
                        <div className="app-versions__breakdown-bar-wrap">
                          <div className="app-versions__breakdown-bar" style={{ width: `${b.percentage}%`, background: b.is_latest ? '#2ECC71' : '#F1C40F' }} />
                        </div>
                        <span className="app-versions__breakdown-count">{b.count} users ({b.percentage}%)</span>
                      </div>
                      {expandedVersion === b.version && b.users?.length > 0 && (
                        <div className="app-versions__breakdown-users">
                          {b.users.map((u) => (
                            <div key={u._id} className="app-versions__breakdown-user" onClick={() => navigate(`/users/${u._id}`)}>
                              <span>{u.firstName} {u.lastName}</span>
                              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New version form */}
            {showForm && (
              <form className="app-versions__form" onSubmit={handleSubmit}>
                <h2>Set New Version</h2>
                <div className="app-versions__form-row">
                  <div className="app-versions__form-field">
                    <label>Version Number</label>
                    <input
                      type="text"
                      placeholder="e.g. 2.1.0"
                      value={newVersion}
                      onChange={(e) => setNewVersion(e.target.value)}
                    />
                  </div>
                  <div className="app-versions__form-field">
                    <label>Platform</label>
                    <select value={newPlatform} onChange={(e) => setNewPlatform(e.target.value)}>
                      <option value="ios">iOS</option>
                      <option value="android">Android</option>
                    </select>
                  </div>
                </div>
                <div className="app-versions__form-toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={forceUpdate}
                      onChange={(e) => setForceUpdate(e.target.checked)}
                    />
                    <span>Force update — users on older versions must update</span>
                  </label>
                </div>
                <Button primaryBtn type="submit" disabled={submitting} showLoading={submitting}>
                  Set Version
                </Button>
              </form>
            )}

            {/* Version history */}
            <div className="app-versions__history">
              <h2>Version History</h2>
              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Skeleton type="row" />
                  <Skeleton type="row" />
                  <Skeleton type="row" />
                </div>
              ) : versions.length === 0 ? (
                <div className="app-versions__empty">No versions set yet</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Platform</th>
                      <th>Force Update</th>
                      <th>Released</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v) => (
                      <tr key={v._id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v.version}</td>
                        <td>
                          <span className={`app-versions__platform-badge app-versions__platform-badge--${v.platform}`}>
                            {v.platform === 'ios' ? 'iOS' : 'Android'}
                          </span>
                        </td>
                        <td>
                          {v.updateRequired ? (
                            <span className="app-versions__force-badge">Required</span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.3)' }}>Optional</span>
                          )}
                        </td>
                        <td>{formatDate(v.releaseDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </article>
      </div>
    </main>
  );
};

export default AppVersions;
